

/**
 * useDownloadImage - Hook for downloading the flow as an image
 * Uses html-to-image library to capture the flow canvas.
 *
 * @module react-flow/hooks/useDownloadImage
 */

import { getNodesBounds, getViewportForBounds, useReactFlow } from '@xyflow/react';
import { useCallback, useState } from 'react';

export interface UseDownloadImageOptions {
  /** Image width (default: auto-calculated from nodes) */
  width?: number;
  /** Image height (default: auto-calculated from nodes) */
  height?: number;
  /** Background color (default: transparent for PNG, white for JPEG) */
  backgroundColor?: string;
  /** Padding around the flow */
  padding?: number;
  /** Default filename without extension */
  defaultFileName?: string;
}

/**
 * useDownloadImage Hook
 *
 * Note: This hook requires the html-to-image library (version 1.11.11 recommended).
 * Install it with: `npm install html-to-image@1.11.11`
 *
 * Usage:
 * ```tsx
 * function MyFlow() {
 *   const { downloadImage, isDownloading } = useDownloadImage({
 *     defaultFileName: 'my-flow',
 *     padding: 50,
 *   });
 *
 *   return (
 *     <>
 *       <ReactFlow ... />
 *       <button
 *         onClick={() => downloadImage('png')}
 *         disabled={isDownloading}
 *       >
 *         {isDownloading ? 'Downloading...' : 'Download as PNG'}
 *       </button>
 *     </>
 *   );
 * }
 * ```
 */
export function useDownloadImage(options: UseDownloadImageOptions = {}) {
  const {
    width: fixedWidth,
    height: fixedHeight,
    backgroundColor,
    padding = 20,
    defaultFileName = 'flow',
  } = options;

  const [isDownloading, setIsDownloading] = useState(false);
  const { getNodes } = useReactFlow();

  const downloadImage = useCallback(
    async (format: 'png' | 'jpeg' | 'svg' = 'png', fileName?: string) => {
      // Dynamically import html-to-image to avoid SSR issues
      let toImage: typeof import('html-to-image').toPng;
      try {
        const htmlToImage = await import('html-to-image');
        switch (format) {
          case 'jpeg':
            toImage = htmlToImage.toJpeg;
            break;
          case 'svg':
            toImage = htmlToImage.toSvg;
            break;
          default:
            toImage = htmlToImage.toPng;
        }
      } catch {
        console.error(
          'html-to-image is not installed. Install with: npm install html-to-image@1.11.11'
        );
        return;
      }

      setIsDownloading(true);

      try {
        // Find the viewport element
        const viewport = document.querySelector('.react-flow__viewport') as HTMLElement;
        if (!viewport) {
          throw new Error('Could not find React Flow viewport');
        }

        // Calculate bounds
        const nodes = getNodes();
        if (nodes.length === 0) {
          throw new Error('No nodes to export');
        }

        const nodesBounds = getNodesBounds(nodes);
        const imageWidth = fixedWidth || nodesBounds.width + padding * 2;
        const imageHeight = fixedHeight || nodesBounds.height + padding * 2;

        // Get the transform for centering the view
        const viewport_ = getViewportForBounds(
          nodesBounds,
          imageWidth,
          imageHeight,
          0.5,
          2,
          padding
        );

        // Create image
        const dataUrl = await toImage(viewport, {
          backgroundColor: backgroundColor || (format === 'jpeg' ? '#ffffff' : undefined),
          width: imageWidth,
          height: imageHeight,
          style: {
            width: `${imageWidth}px`,
            height: `${imageHeight}px`,
            transform: `translate(${viewport_.x}px, ${viewport_.y}px) scale(${viewport_.zoom})`,
          },
        });

        // Download
        const link = document.createElement('a');
        link.download = `${fileName || defaultFileName}.${format}`;
        link.href = dataUrl;
        link.click();
      } catch (error) {
        console.error('Failed to download image:', error);
      } finally {
        setIsDownloading(false);
      }
    },
    [getNodes, fixedWidth, fixedHeight, backgroundColor, padding, defaultFileName]
  );

  return {
    downloadImage,
    isDownloading,
  };
}

export default useDownloadImage;
